/**
 * /operations/payroll/off-cycle — Session 3B PR 12.
 * Owning agent: Rawatib.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertOctagon, CheckCircle2, ChevronRight, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { OffCycleItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StateFilter = "all" | "pending" | "approved" | "disbursed" | "denied";

function _stateToneClass(s: OffCycleItem["state"]): string {
  if (s === "disbursed") return "bg-emerald-100 text-emerald-800";
  if (s === "denied") return "bg-rose-100 text-rose-800";
  if (s === "approved") return "bg-brand-blue/15 text-brand-deep";
  return "bg-amber-100 text-amber-800";
}

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function OffCycleDisbursementsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [stateFilter, setStateFilter] = useState<StateFilter>("pending");

  const list = useQuery({
    queryKey: ["session3b-off-cycle", legalEntityId, stateFilter],
    queryFn: () =>
      session3bApi.listOffCycle(
        legalEntityId ?? "",
        stateFilter === "all" ? undefined : stateFilter,
      ),
    enabled: !!legalEntityId,
  });

  const items: OffCycleItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const pending = items.filter((r) => r.state === "pending");
    const sumPending = pending.reduce((a, r) => a + r.amount_sar, 0);
    return {
      total: items.length,
      attention: pending.length,
      critical: 0,
      sumPending,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("payroll.off_cycle.title", "Off-Cycle Disbursements")}
          subtitle={t(
            "payroll.off_cycle.subtitle",
            "Out-of-cycle payments: travel advances · loan disbursements · ad-hoc bonuses · final settlements. Rawatib tracks the queue.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["pending", "approved", "disbursed", "denied", "all"] as StateFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStateFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  stateFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`payroll.off_cycle.filter.${f}`, f)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "payroll.off_cycle.cta.initiate_note",
                  "Initiate Off-Cycle drawer: employee + type + amount + reason + approval routing.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("payroll.off_cycle.cta.initiate", "Initiate Off-Cycle Disbursement")}
          </button>
        </div>

        {counts.attention > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<AlertOctagon className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "payroll.off_cycle.suggestion.pending_headline",
              `${counts.attention} pending disbursement${counts.attention === 1 ? "" : "s"} · total SAR ${_fmtSar(counts.sumPending)}.`,
            )}
            body={t(
              "payroll.off_cycle.suggestion.pending_body",
              "Mutaba'aa watches Article 90 (1/3 wage-deduction cap) before each disbursement clears. Rawatib suggests bulk-approving the cleared ones.",
            )}
            primaryAction={{
              label: t("payroll.off_cycle.suggestion.review", "Review queue"),
              onClick: () => setStateFilter("pending"),
            }}
            suggestionKey="off-cycle-pending"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.off_cycle.suggestion.empty_headline",
              "No off-cycle disbursements in this state.",
            )}
            body={t(
              "payroll.off_cycle.suggestion.empty_body",
              "Rawatib notes: travel advances and loan disbursements route here automatically when initiated from their respective workflows.",
            )}
            primaryAction={{
              label: t("payroll.off_cycle.suggestion.view_all", "View all"),
              onClick: () => setStateFilter("all"),
            }}
            suggestionKey="off-cycle-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("payroll.off_cycle.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("payroll.off_cycle.col.type", "Type")}</th>
                  <th className="px-3 py-2">{t("payroll.off_cycle.col.amount", "Amount (SAR)")}</th>
                  <th className="px-3 py-2">{t("payroll.off_cycle.col.requested", "Requested")}</th>
                  <th className="px-3 py-2">{t("payroll.off_cycle.col.state", "State")}</th>
                  <th className="px-3 py-2">{t("payroll.off_cycle.col.bank", "Bank")}</th>
                  <th className="px-3 py-2">{t("payroll.off_cycle.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.disbursement_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">{row.disbursement_type}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "payroll.off_cycle.trace.amount",
                              `amount_sar = workflow_event.payload_diff.amount_sar for this disbursement. Mutaba'aa validates Article 90 cap before approving — combined loan + advance deductions ≤ 1/3 of monthly wage.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono">{_fmtSar(row.amount_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.requested_at}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_stateToneClass(row.state)}`}
                      >
                        {row.state}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">{row.bank_status}</td>
                    <td className="px-3 py-2">
                      {(row.state === "pending" || row.state === "approved") && (
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t(
                                "payroll.off_cycle.action.next_note",
                                "Advance via the M3X.PAYROLL off-cycle workflow. The state-machine endpoint is a separate workstream; today the surface captures the intent.",
                              ),
                            )
                          }
                          className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] opacity-50 transition-opacity group-hover:opacity-100 hover:bg-brand-blue/10"
                        >
                          {row.state === "pending"
                            ? t("payroll.off_cycle.action.approve", "Approve")
                            : t("payroll.off_cycle.action.disburse", "Disburse")}
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("payroll.off_cycle.title", "Off-Cycle Disbursements"),
            window_label: `pending SAR ${_fmtSar(counts.sumPending)}`,
          }}
        />
      </aside>
    </div>
  );
}
